import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  TextField,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Button,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ShieldIcon from '@mui/icons-material/Shield';
import { AiService, type AiConfigData, type AiInsightCard } from '@/services/aiService';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { getErrorMessage } from '@/utils/helpers';
import { API_BASE_URL } from '@/constants';
import { saveAndOpenBlob } from '@/mobile/services/file.service';

export interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  toolsExecuted?: (string | any)[];
  reportDownloadUrl?: string;
  reportFormat?: 'PDF' | 'XLSX' | 'CSV';
  tokenUsage?: { totalTokens: number };
  isError?: boolean;
  correlationId?: string;
  isDraftAction?: boolean;
}

export const SAMPLE_PROMPTS = [
  'What is our total outstanding and which properties have dues >90 days?',
  'Why did expenses increase this month?',
  'How much collection should we expect next month?',
  'Will we have enough cash next month?',
  'Prepare an AGM financial summary statement.',
  'Which maintenance requests breached SLA?',
  'Show me vendors whose contracts expire next month.',
  'Draft a notice about water shortage and conservation.',
];

export interface AiChatInterfaceProps {
  mode?: 'page' | 'drawer';
  onNavigateToConfig?: () => void;
  onClearThreadRef?: React.MutableRefObject<(() => void) | null>;
  onConfigLoaded?: (config: AiConfigData | null) => void;
}

export function AiChatInterface({
  mode = 'page',
  onNavigateToConfig,
  onClearThreadRef,
  onConfigLoaded,
}: AiChatInterfaceProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const tenant = useAuthStore((state) => state.tenant);

  const [aiConfig, setAiConfig] = useState<AiConfigData | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [insights, setInsights] = useState<AiInsightCard[]>([]);

  const [inputMessage, setInputMessage] = useState('');
  const [reportFormat, setReportFormat] = useState<'NONE' | 'PDF' | 'XLSX' | 'CSV'>('NONE');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      sender: 'assistant',
      text: `Hello ${user?.firstName || user?.name || 'Executive Member'}! I am your official VYORA AI Society Copilot & Predictive Analytics Assistant.
I am grounded in live, authorized society records. You can ask me about:
• Financial Summaries & Expense Analysis ("Why did expenses increase this month?")
• Predictive Analytics & Cash Flow ("How much collection should we expect next month?")
• Overdue Collections ("Which properties have dues older than 90 days?")
• Vendor Contracts & Maintenance SLA Breaches
• AGM Financial Summaries & Notice Drafting ("Draft a notice about water conservation")

How can I assist you today?`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);

  const isPresidentOrVP = user?.roles?.some((r: string) =>
    ['PRESIDENT', 'VICE_PRESIDENT'].includes(r.toUpperCase()),
  );

  const loadConfigAndInsights = async () => {
    try {
      const res = await AiService.getConfig();
      if (res.data) {
        const d: any = res.data;
        const cfg: AiConfigData = {
          enabled: Boolean(d.isEnabled ?? d.enabled),
          provider: d.provider || 'GEMINI',
          model: d.model || 'gemini-1.5-flash',
          hasApiKey: Boolean(d.isConfigured || d.hasApiKey),
          lastTestedAt: d.lastTestedAt,
          lastTestStatus: d.lastTestStatus,
          lastTestLatencyMs: d.lastTestLatencyMs,
          totalTokensUsed: d.usage?.totalTokens || 0,
          totalQueries: d.usage?.totalRequests || 0,
        };
        setAiConfig(cfg);
        onConfigLoaded?.(cfg);
      }
    } catch {
      // Non-blocking
    } finally {
      setConfigLoading(false);
    }

    try {
      const insightRes = await AiService.getInsights();
      if (insightRes?.data && Array.isArray(insightRes.data)) {
        setInsights(insightRes.data);
      }
    } catch {
      // Non-blocking
    }
  };

  useEffect(() => {
    loadConfigAndInsights();
  }, []);

  const handleClearChat = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        sender: 'assistant',
        text: `Conversation thread reset. How can I assist you with ${tenant?.name || 'your society'} records?`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
    setErrorMessage(null);
  }, [tenant?.name]);

  useEffect(() => {
    if (onClearThreadRef) {
      onClearThreadRef.current = handleClearChat;
    }
  }, [handleClearChat, onClearThreadRef]);

  const scrollToBottom = useCallback(() => {
    if (typeof chatEndRef.current?.scrollIntoView === 'function') {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  const handleSendMessage = async (customPrompt?: string) => {
    const textToSend = (customPrompt || inputMessage).trim();
    if (!textToSend || loading) return;

    setErrorMessage(null);
    const userMsgId = `user-${Date.now()}`;
    const newMessages: ChatMessage[] = [
      ...messages,
      {
        id: userMsgId,
        sender: 'user',
        text: textToSend,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ];

    setMessages(newMessages);
    setInputMessage('');
    setLoading(true);

    try {
      const history = newMessages
        .filter((m) => m.id !== 'welcome' && !m.isError)
        .slice(-6)
        .map((m) => ({
          role: m.sender,
          content: m.text,
        }));

      const res = await AiService.sendMessage({
        message: textToSend,
        conversationHistory: history,
        includeReport: reportFormat !== 'NONE',
        reportFormat: reportFormat !== 'NONE' ? reportFormat : undefined,
      });

      if (res.data) {
        const d: any = res.data;
        setMessages((prev) => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            sender: 'assistant',
            text: d.reply || 'No response text received.',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            toolsExecuted: d.toolsExecuted || [],
            reportDownloadUrl: d.report?.downloadUrl || d.reportDownloadUrl,
            reportFormat: d.report?.format || d.reportFormat,
            tokenUsage: d.usage || d.tokenUsage,
            correlationId: d.correlationId,
            isDraftAction: d.isDraftAction,
          },
        ]);
      } else {
        throw new Error(res.message || 'Failed to get response from AI Assistant');
      }
    } catch (err: any) {
      const errMsg = getErrorMessage(err, 'Failed to process AI query. Please verify configuration and try again.');
      setErrorMessage(errMsg);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          sender: 'assistant',
          text: `⚠️ ${errMsg}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isError: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (downloadUrl: string, format = 'XLSX', msgId?: string) => {
    try {
      if (msgId) setDownloadingReportId(msgId);
      let cleanPath = downloadUrl.replace(API_BASE_URL, '').replace(/^\/api\/v1/, '');
      if (!cleanPath.startsWith('/')) {
        cleanPath = `/${cleanPath}`;
      }

      const response = await apiClient.get(cleanPath, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const ext = format.toLowerCase() === 'xlsx' ? 'xlsx' : format.toLowerCase() === 'pdf' ? 'pdf' : 'csv';
      const filename = `society_ai_report_${Date.now()}.${ext}`;
      await saveAndOpenBlob(blob, filename);
    } catch (err: any) {
      setErrorMessage(getErrorMessage(err, 'Failed to download report'));
    } finally {
      if (msgId) setDownloadingReportId(null);
    }
  };

  const isDrawer = mode === 'drawer';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Proactive AI Insights Accordion if Insights exist */}
      {insights.length > 0 && !isDrawer && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpIcon color="primary" sx={{ fontSize: '1.1rem' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>
                Proactive AI Insights ({insights.length} active notifications)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
              {insights.map((ins) => (
                <Chip
                  key={ins.id}
                  label={`${ins.title}: ${ins.metricHeadline}`}
                  color={ins.severity === 'CRITICAL' ? 'error' : ins.severity === 'WARNING' ? 'warning' : 'info'}
                  variant="outlined"
                  size="small"
                  onClick={() => handleSendMessage(`Tell me more about: ${ins.title}`)}
                  sx={{ cursor: 'pointer', mb: 0.5 }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Society AI Disabled / Unconfigured Warning Notice */}
      {!configLoading && aiConfig && (!aiConfig.enabled || !aiConfig.hasApiKey) && (
        <Card
          sx={{
            m: isDrawer ? 1.5 : { xs: 1, sm: 2 },
            p: 1.5,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: isDrawer ? 'column' : 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: isDrawer ? 'flex-start' : 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <InfoOutlinedIcon color="warning" sx={{ mt: 0.25, fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  {!aiConfig.hasApiKey ? 'AI Key Not Configured' : 'AI Assistant is Disabled'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                  {!aiConfig.hasApiKey
                    ? 'Enter an API key to activate AI responses.'
                    : 'Enable AI in Configuration settings.'}
                </Typography>
              </Box>
            </Stack>
            {isPresidentOrVP && (
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => {
                  if (onNavigateToConfig) {
                    onNavigateToConfig();
                  } else {
                    navigate('/settings/ai-config');
                  }
                }}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  py: 0.4,
                  alignSelf: { xs: 'stretch', sm: isDrawer ? 'stretch' : 'auto' },
                }}
              >
                Configure AI
              </Button>
            )}
          </Stack>
        </Card>
      )}

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ m: isDrawer ? 1.5 : { xs: 1, sm: 2 }, flexShrink: 0, fontSize: '0.8125rem' }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Main Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: isDrawer ? 2 : { xs: 1.5, sm: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          minHeight: 0,
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(20,24,33,0.95) 0%, rgba(15,18,25,1) 100%)'
              : 'linear-gradient(180deg, #F8FAFC 0%, #EEF2F6 100%)',
        }}
      >
        {messages.map((msg) => {
          const isUser = msg.sender === 'user';
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                elevation={isUser ? 2 : 1}
                sx={{
                  maxWidth: isDrawer ? '90%' : { xs: '92%', sm: '82%', md: '75%' },
                  p: isDrawer ? 1.5 : { xs: 1.5, sm: 2 },
                  borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: isUser
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    : theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.85)
                      : '#FFFFFF',
                  color: isUser ? '#FFFFFF' : theme.palette.text.primary,
                  border: isUser
                    ? 'none'
                    : `1px solid ${msg.isError ? theme.palette.error.main : alpha(theme.palette.divider, 0.6)}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {!isUser ? (
                    <AutoAwesomeIcon
                      sx={{
                        fontSize: 15,
                        color: msg.isError ? theme.palette.error.main : theme.palette.primary.main,
                      }}
                    />
                  ) : null}
                  <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.85, fontSize: '0.75rem' }}>
                    {isUser ? 'You' : 'VYORA Society Copilot'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, ml: 'auto !important', fontSize: '0.7rem' }}>
                    {msg.timestamp}
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.55,
                    fontSize: isDrawer ? '0.85rem' : { xs: '0.88rem', sm: '0.92rem' },
                  }}
                >
                  {msg.text}
                </Typography>

                {/* Draft Notice Action Callout */}
                {msg.isDraftAction && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                      border: `1px solid ${theme.palette.warning.main}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EditNoteIcon color="warning" sx={{ fontSize: '1.2rem' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                          Draft Saved. Requires Review & Publishing.
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => navigate('/notices')}
                        sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.2 }}
                      >
                        Go to Notices
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* Tool Executions Badge List */}
                {!isUser && msg.toolsExecuted && msg.toolsExecuted.length > 0 && (
                  <Box sx={{ mt: 1.25, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                    >
                      <ShieldIcon sx={{ fontSize: '0.8rem', color: 'success.main' }} />
                      Grounded in Authorized Subsystems:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {msg.toolsExecuted.map((t, idx) => {
                        const name = typeof t === 'string' ? t : t.toolName || 'Tool';
                        return (
                          <Chip
                            key={idx}
                            label={name}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: alpha(theme.palette.primary.main, 0.06),
                              borderColor: alpha(theme.palette.primary.main, 0.2),
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* Downloadable Report Callout */}
                {msg.reportDownloadUrl && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                      border: `1px solid ${theme.palette.success.main}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {msg.reportFormat === 'PDF' ? (
                        <PictureAsPdfIcon color="error" sx={{ fontSize: '1.2rem' }} />
                      ) : msg.reportFormat === 'XLSX' ? (
                        <TableChartIcon color="success" sx={{ fontSize: '1.2rem' }} />
                      ) : (
                        <DescriptionIcon color="primary" sx={{ fontSize: '1.2rem' }} />
                      )}
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.75rem' }}>
                          Official {msg.reportFormat || 'Document'} Ready
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Includes complete dataset breakdown
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={
                        downloadingReportId === msg.id ? (
                          <CircularProgress size={12} color="inherit" />
                        ) : (
                          <DownloadIcon sx={{ fontSize: '0.85rem !important' }} />
                        )
                      }
                      disabled={downloadingReportId === msg.id}
                      onClick={() => handleDownload(msg.reportDownloadUrl!, msg.reportFormat, msg.id)}
                      sx={{ fontSize: '0.725rem', textTransform: 'none', py: 0.4 }}
                    >
                      Download {msg.reportFormat || 'File'}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          );
        })}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                borderRadius: '16px 16px 16px 4px',
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : '#FFFFFF',
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <CircularProgress size={16} color="primary" />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                Executing authorized tools & synthesizing evidence...
              </Typography>
            </Paper>
          </Box>
        )}
        <div ref={chatEndRef} />
      </Box>

      {/* Suggested Prompts & Quick Actions Bar */}
      <Box
        sx={{
          p: isDrawer ? 1 : 1.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700, fontSize: '0.7rem' }}>
          💡 Copilot Quick Queries:
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.5, '::-webkit-scrollbar': { height: 3 } }}>
          {SAMPLE_PROMPTS.map((prompt, idx) => (
            <Chip
              key={idx}
              label={prompt}
              size="small"
              onClick={() => handleSendMessage(prompt)}
              disabled={loading}
              sx={{
                cursor: 'pointer',
                fontSize: '0.725rem',
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                '&:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                },
              }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.68rem' }}>
            Attached Export:
          </Typography>
          {(['NONE', 'PDF', 'XLSX', 'CSV'] as const).map((fmt) => (
            <Chip
              key={fmt}
              label={fmt === 'NONE' ? 'Text Only' : fmt}
              size="small"
              variant={reportFormat === fmt ? 'filled' : 'outlined'}
              color={reportFormat === fmt ? 'primary' : 'default'}
              onClick={() => setReportFormat(fmt)}
              disabled={loading}
              sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Box>

      {/* Input Message Form */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        sx={{
          p: isDrawer ? 1.5 : 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Ask Copilot about dues, expenses, forecasts, meetings, SLA..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={loading}
            multiline
            maxRows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.875rem',
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!inputMessage.trim() || loading}
            sx={{
              minWidth: 44,
              width: 44,
              height: 40,
              borderRadius: 2,
              p: 0,
            }}
            aria-label="Send Message"
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: '1.1rem' }} />}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
